import React from 'react';
import { Box, LinearProgress, Typography } from '@mui/material';

const LISTENER_ORDER = ['Patient', 'Doctor', 'System'];

function ProbabilityBars({ title, answer, order }) {
  const probabilities = answer?.probabilities && typeof answer.probabilities === 'object' ? answer.probabilities : {};
  const labels = order || Object.keys(probabilities).sort((a, b) => (probabilities[b] || 0) - (probabilities[a] || 0));
  if (labels.length === 0) return null;
  return (
    <Box sx={{ mt: 0.75 }}>
      <Typography variant="caption" component="div" color="text.secondary" sx={{ fontWeight: 600, lineHeight: 1.55 }}>
        {title}
        {': '}
        {answer?.choice || 'N/A'}
        {typeof answer?.confidence === 'number' ? ` (confidence ${answer.confidence.toFixed(2)})` : ''}
      </Typography>
      {labels.map((label) => {
        const pct = Math.round((Number(probabilities[label]) || 0) * 1000) / 10;
        const chosen = label === answer?.choice;
        return (
          <Box key={label} sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.25 }}>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ width: 96, flexShrink: 0, fontWeight: chosen ? 700 : 400 }}
            >
              {label}
            </Typography>
            <LinearProgress
              variant="determinate"
              value={pct}
              color={chosen ? 'primary' : 'inherit'}
              aria-label={`${label} probability ${pct}%`}
              sx={{ flex: 1, height: 6, borderRadius: 3, opacity: chosen ? 1 : 0.45 }}
            />
            <Typography variant="caption" color="text.secondary" sx={{ width: 44, textAlign: 'right', flexShrink: 0 }}>
              {`${pct}%`}
            </Typography>
          </Box>
        );
      })}
    </Box>
  );
}

/** Expandable detail under the Target chip when Jev chose the listener. */
export default function JevTargetingBreakdown({ detail }) {
  if (!detail || typeof detail !== 'object') return null;
  const listOnlyNone = !detail.patient_list || detail.patient_list.choice === 'None';

  return (
    <Box
      sx={{
        mt: 1,
        p: 1.25,
        borderRadius: 1,
        bgcolor: (theme) => (theme.palette.mode === 'dark' ? 'grey.900' : 'grey.50'),
        border: '1px solid',
        borderColor: 'divider',
      }}
    >
      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700, letterSpacing: 0.4, display: 'block' }}>
        Intent 3.0 targeting
      </Typography>
      {detail.ok ? (
        <>
          <ProbabilityBars title="Listener" answer={detail.listener} order={LISTENER_ORDER} />
          {listOnlyNone ? (
            <Typography variant="caption" component="div" color="text.secondary" sx={{ mt: 0.75, lineHeight: 1.55 }}>
              <Box component="span" sx={{ fontWeight: 600 }}>Patient list</Box>
              {': None'}
            </Typography>
          ) : (
            <ProbabilityBars title="Patient list (sends to System)" answer={detail.patient_list} />
          )}
          {detail.clinical_request?.choice && (
            <ProbabilityBars title="Diagnosis / consult (Doctor only)" answer={detail.clinical_request} />
          )}
        </>
      ) : (
        <Typography variant="caption" component="div" color="error" sx={{ mt: 0.5, lineHeight: 1.55 }}>
          {`Intent 3.0 targeting failed${detail.error ? `: ${detail.error}` : ''}. The listener came from the embedding results instead.`}
        </Typography>
      )}
      <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mt: 0.75, lineHeight: 1.45 }}>
        {`${typeof detail.duration_ms === 'number' ? `${detail.duration_ms}ms` : 'N/A'}${detail.model ? ` · ${detail.model}` : ''}. `}
        TypeSafe runs in parallel with embedding, so this time overlaps the Intent time.
      </Typography>
    </Box>
  );
}
